import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { dispatch, getState } from "../../../../state/store";
import { setIsRolesReassigned, setSelectedApp } from "../../../../state/ducks";

import {
  appUserService,
  groupsService,
  t,
  notificationService,
  appsService,
  log
} from "../../../../services";
import { appManageTypes, appUserRoles, strCode } from "../../../../consts";

import { RolesTabModal, RolesTabTable } from "../../../../containers";
import { FlexContainers, Paragraph2, WrapperContainer } from "../../../../components";
import { selectIsRolesReassigned } from "../../../../state/selectors";

const RolesTab = () => {
  const selectedApp = useSelector((store) => store.apps.selectedApp);
  const manageType = useSelector((store) => store.apps.manageType);
  const defaultFeatures = useSelector((store) => store.apps?.selectedApp.defaultSettings);

  const [isMarketsModalActive, setIsMarketsModalActive] = useState(false);
  const [role, setRole] = useState("");
  const [roleAdditionFlag, setRoleAdditionFlag] = useState(false);
  const [isGroupsLoading, setIsGroupsLoading] = useState(false);
  const [isGroupsError, setIsGroupsError] = useState(false);

  const fetchGroups = useCallback(async () => {
    setIsGroupsLoading(true);

    try {
      const group = await groupsService.fetchSelectedGroup(selectedApp.appType);

      dispatch(setSelectedApp({ ...selectedApp, groupConfigs: group }));
    } catch (err) {
      log.error("RolesTab: Failed to fetch groups", err);

      setIsGroupsError(true);
      notificationService.showError({
        title: t(strCode.GET_APP_GROUP_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      setIsGroupsLoading(false);
    }
  }, []);

  useEffect(async () => {
    const isRolesReassigned = selectIsRolesReassigned()(getState());

    if (isRolesReassigned || manageType !== appManageTypes.FROM_EXISTING) {
      await fetchGroups();
    }
  }, []);

  useEffect(async () => {
    const isRolesReassigned = selectIsRolesReassigned()(getState());

    if (!isRolesReassigned && manageType === appManageTypes.FROM_EXISTING) {
      await Promise.all(
        selectedApp.groupConfigs.map((config) => {
          return appsService.createAppRoles({
            ...config,
            appType: selectedApp.appType,
            features: defaultFeatures
              .filter((feature) => feature.value)
              .map((feature) => feature.code)
          });
        })
      );

      dispatch(setIsRolesReassigned(true));
    }
  }, [selectedApp]);

  const isReadOnlyAccess = useMemo(() => {
    return !(
      appUserService.hasRole(appUserRoles.Admin) ||
      appUserService.hasRole(appUserRoles.ApplicationManager)
    );
  }, []);

  const onModalClose = () => {
    setIsMarketsModalActive(false);
    setRoleAdditionFlag(false);
    setRole("");
  };

  return (
    <WrapperContainer mt={24} pr={24} Container={FlexContainers.FlexColumn} width="100%">
      {isGroupsError ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={50}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.MARKETS_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : (
        <>
          <RolesTabTable
            openModal={() => setIsMarketsModalActive(true)}
            setRoleAdditionFlag={setRoleAdditionFlag}
            setRole={setRole}
            isReadOnlyAccess={isReadOnlyAccess}
            isLoading={isGroupsLoading}
          />

          <RolesTabModal
            isActive={isMarketsModalActive}
            onModalClose={onModalClose}
            roleAdditionFlag={roleAdditionFlag}
            role={role}
            setRole={setRole}
            isReadOnlyAccess={isReadOnlyAccess}
          />
        </>
      )}
    </WrapperContainer>
  );
};

export default RolesTab;
